import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, MailIcon, AlertCircleIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Button } from './Button';
import { UserRole } from '../pages/Users';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
interface LoginProps {
  onLogin: (username: string, role: UserRole) => void;
}
export function Login({
  onLogin
}: LoginProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Initialize default admin user if no users exist
  const initializeDefaultUsers = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      if (usersSnapshot.empty) {
        console.log('No users found, creating default admin user...');
        // Create default admin user
        await addDoc(usersCollection, {
          email: 'daniel@lykke-el.dk',
          password: 'LYKKE1608!',
          role: 'Admin',
          createdAt: new Date(),
          isDefaultAdmin: true // Mark as default admin - cannot be deleted
        });
        console.log('Default admin user created successfully');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error initializing default users:', err);
      return false;
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      // First, check if we need to initialize default users
      await initializeDefaultUsers();
      // Get users from Firestore
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      if (usersSnapshot.empty) {
        setError('Kunne ikke oprette brugere. Kontakt support.');
        setIsLoading(false);
        return;
      }
      const users: any[] = [];
      usersSnapshot.forEach(doc => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });
      console.log('Found users:', users.length);
      // Check if user exists and password matches
      const user = users.find(u => u.email === username && u.password === password);
      if (user) {
        console.log('Login successful for:', username);
        // Store authentication state
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('username', username);
        localStorage.setItem('password', password);
        // Call onLogin with username and role
        onLogin(username, user.role as UserRole);
      } else {
        console.log('Login failed - invalid credentials');
        setError('Forkert email eller adgangskode');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Fejl ved login. Prøv igen.');
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen w-full bg-gradient-to-br from-stone-50 via-teal-50/30 to-stone-50 flex items-center justify-center p-4" data-id="element-30" data-mp="28wpet0le1qmy">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md" data-id="element-31" data-mp="2noprfw4to1er">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8" data-id="element-32" data-mp="1q0z8x2ac4a3s">
          <h1 className="text-3xl font-bold text-stone-900 mb-2" data-id="element-33" data-mp="23z5o317i7le4">
            Velkommen tilbage
          </h1>
          <p className="text-stone-600" data-id="element-34" data-mp="1zwsrp3enczu0">
            Log ind for at få adgang til dit dashboard
          </p>
        </div>

        {/* Login Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3,
        duration: 0.5
      }} className="bg-white rounded-2xl shadow-xl border border-stone-200 p-8" data-id="element-35" data-mp="3ffvv2mjr4h8s">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-36" data-mp="jgn05bwr7cin">
            {/* Error Message */}
            {error && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-37" data-mp="oicasqg2do67">
                <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-38" data-mp="177setnqyjyrd" />
                <p className="text-sm text-red-800" data-id="element-39" data-mp="1c6c0dn1n38j7">{error}</p>
              </motion.div>}

            {/* Username Field */}
            <div data-id="element-40" data-mp="1h6hwbfexzh73">
              <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-41" data-mp="1h5yjgd45fizx">
                Email
              </label>
              <div className="relative" data-id="element-42" data-mp="31l4nweejyuwp">
                <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-43" data-mp="2wo4qervrr0h7" />
                <input type="email" value={username} onChange={e => setUsername(e.target.value)} placeholder="din@email.dk" required disabled={isLoading} className="w-full pl-12 pr-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-44" data-mp="2rqcp5798i20c" />
              </div>
            </div>

            {/* Password Field */}
            <div data-id="element-45" data-mp="70dkb6nhk91y">
              <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-46" data-mp="70x3eqovh3hk">
                Adgangskode
              </label>
              <div className="relative" data-id="element-47" data-mp="1wfo9ja5tz1ur">
                <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-48" data-mp="1wgpvwasarars" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required disabled={isLoading} className="w-full pl-12 pr-12 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-49" data-mp="21erc8nyc8vzk" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isLoading} aria-label={showPassword ? 'Skjul adgangskode' : 'Vis adgangskode'} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-md transition-colors disabled:opacity-50" data-id="element-50" data-mp="18rermjbqmy8w">
                  {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-51" data-mp="118fdas34vwwe" /> : <EyeIcon className="w-5 h-5" data-id="element-52" data-mp="wfm7e0vxiiiq" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button type="submit" variant="primary" disabled={isLoading} className="w-full" data-id="element-53" data-mp="1597jtdkmfnoz">
              {isLoading ? <span className="flex items-center justify-center gap-2" data-id="element-54" data-mp="1f75ae7pydwkr">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-55" data-mp="1f5179rzvp1lj" />
                  Logger ind...
                </span> : 'Log ind'}
            </Button>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="text-center text-sm text-stone-500 mt-6" data-id="element-56" data-mp="39vs8bevjqg4z">
          Beskyttet adgang • Kun for autoriserede brugere
        </motion.p>
      </motion.div>
    </div>;
}